export type Brief = {
  id: string;
  type: "hidden_gems" | "trending_videos";
  enabled: boolean;
  day: string;
  hour: number;
};
export type CoachStatus = {
  /**
   * True on the one response where the server filled an empty seat with the
   * TikTok on the creator's BMC app profile (and started the build).
   */
  handle_from_app?: boolean;
  seat: {
    exists: boolean;
    /** Entitlement only. Anything that is not `cancelled` is entitled. */
    status: "active" | "cancelled";
    /** Empty until the creator picks one in onboarding. */
    tiktok_username: string;
    /** The TikTok profile's name and picture (stored copy); null until the worker has read it. */
    tiktok_display_name?: string | null;
    tiktok_avatar_url?: string | null;
    /**
     * `awaiting_handle`: paid, but no TikTok account chosen yet — the state a
     * purchase with no checkout answer leaves behind (COACH_PORTAL_V1_PLAN).
     * Nothing builds until onboarding sets the handle via fix-handle.
     */
    onboarding_status: "ready" | "pending_enrichment" | "processing" | "failed" | "awaiting_handle";
    corpus_videos: number;
    corpus_newest: string | null;
    dna_built: boolean;
    connector_url: string;
  };
  app_link: { connected: boolean; showcase_ok: boolean };
  briefs: Brief[];
  billing: {
    plan: "monthly" | "annual" | "creator_pass";
    manage_url: string | null;
  };
  /** The buyer's own Whop referral link (?a=handle) and the recurring commission it pays. */
  affiliate: { link: string | null; commission_pct: number };
};
/** One row of the Billing page's payment history (`GET billing`). Amounts are in currency units, not cents. */
export type BillingPayment = {
  id: string;
  date: string | null;
  description: string;
  amount: number | null;
  refunded_amount: number;
  currency: string;
  status: "paid" | "refunded" | "partially_refunded" | "failed" | "pending";
  card_brand: string | null;
  card_last4: string | null;
};
/** Must match CANCEL_REASONS in the worker (src/billing.ts) and its DB check constraint. */
export const CANCEL_REASONS = [
  { value: "too_expensive", label: "It's too expensive for me right now" },
  { value: "not_using_enough", label: "I'm not using it enough" },
  { value: "not_helping", label: "The coaching isn't helping my content" },
  { value: "setup_trouble", label: "I couldn't get it working in Claude" },
  { value: "missing_features", label: "It's missing something I need" },
  { value: "switching_tool", label: "I'm switching to another tool" },
  { value: "taking_a_break", label: "I'm taking a break from TikTok Shop" },
  { value: "other", label: "Other" },
] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number]["value"];
/** `GET billing` — the seat's Whop subscription, as stored by the worker from Whop's webhooks. */
/** GET /api/self/affiliate (bmc-coach-mcp src/affiliates.ts). Whop is the authority; this mirrors it. */
export type AffiliateSummary = {
  /** False until Whop has a username for this buyer: no link can credit anyone before that. */
  enrolled: boolean;
  link: string | null;
  code: string | null;
  /** Whop's override for this affiliate, else the rate actually paid, else the 35% default. */
  commission_pct: number;
  /** Whop's totals; null when Whop could not be reached and nothing is cached. */
  stats: {
    referrals: number | null;
    active_referrals: number | null;
    earnings_usd: number | null;
    monthly_recurring_usd: number | null;
    retention_pct: number | null;
  } | null;
  stats_as_of: string | null;
  /** whop: Whop's totals. no_referrals_yet: Whop has no affiliate record yet. unavailable: Whop could not be read. */
  stats_source?: "whop" | "no_referrals_yet" | "unavailable";
  /** Commission on the payments we recorded, for when Whop's totals are unavailable. */
  recorded_earnings_usd: number;
  referrals: { name: string; tiktok_added?: boolean; plan: "annual" | "monthly"; status: "active" | "cancelled"; joined_at: string | null; earned_usd: number }[];
  commissions: { date: string | null; payment_usd: number | null; commission_usd: number; kind: "first_payment" | "renewal"; reversed: boolean }[];
  /** Where earnings are withdrawn: sign in with the purchase email, then profile → Affiliates. */
  whop_url: string;
};
export type BillingSummary = {
  plan: "monthly" | "annual" | "creator_pass";
  /** Entitlement, as on coach-status. */
  status: "active" | "cancelled";
  /** Whop's own membership status (active, trialing, past_due, canceled, expired, …); null until synced. */
  membership_status: string | null;
  cancel_at_period_end: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  /** Whop's label, e.g. "$47.00 / month". */
  renewal_price: string | null;
  payment_method: { brand: string | null; last4: string | null } | null;
  /** Whop's hosted membership page (also shows Cancel and Transfer; not linked from the portal). */
  manage_url: string | null;
  /** Whop's saved-cards page, where "Update payment method" goes. No Cancel or Transfer there. */
  payment_methods_url: string | null;
  can_cancel: boolean;
  can_resume: boolean;
  payments: BillingPayment[];
};
/** `GET lookup-handle?handle=` — the public profile behind a typed username. */
export type HandleLookup = {
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  public_videos: number | null;
};
export class CoachError extends Error {
  constructor(
    message: string,
    public status: number,
    /** The worker's machine-readable reason (e.g. `handle_taken_coach`), when it sent one. Never shown as-is. */
    public code?: string,
  ) {
    super(message);
  }
}
const invalid = (message: string) => new CoachError(message, 422, "invalid_handle");
/**
 * A typed username → the bare handle, or a CoachError saying exactly what is
 * wrong with it. Links and emails are refused, never guessed from: the
 * creator confirms the account in the next step, and it must be the one they
 * meant to type.
 */
export function normalizeHandle(value: string) {
  const raw = value.trim();
  if (!raw) throw invalid("Enter your TikTok username.");
  if (/^https?:\/\/|tiktok\.com/i.test(raw)) {
    const fromLink = /tiktok\.com\/@([A-Za-z0-9._]{2,24})/i.exec(raw)?.[1];
    throw invalid(
      fromLink
        ? `Enter just the username, not the link. From that link, your username looks like @${fromLink}.`
        : "Enter just your TikTok username, like @yourname, not a link.",
    );
  }
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(raw))
    throw invalid("That looks like an email address. Enter your TikTok username instead, like @yourname.");
  const handle = raw.replace(/^@+/, "");
  if (/\s/.test(handle)) throw invalid("TikTok usernames can't contain spaces.");
  if (!/^[A-Za-z0-9._]+$/.test(handle))
    throw invalid("TikTok usernames only use letters, numbers, underscores (_) and periods (.).");
  if (handle.length < 2) throw invalid("TikTok usernames are at least 2 characters.");
  if (handle.length > 24) throw invalid("TikTok usernames are at most 24 characters.");
  if (handle.endsWith(".")) throw invalid("TikTok usernames can't end with a period.");
  return handle;
}
/**
 * Fixed copy for the worker's handle refusals (lookup-handle and fix-handle
 * share the codes). Null when the code is unknown, so the caller keeps its
 * generic message.
 */
function handleMessage(e: CoachError, handle: string): string | null {
  const at = `@${handle}`;
  switch (e.code) {
    case "handle_taken_coach":
      return `${at} is already connected to another BMC Coach. If this is your TikTok, contact support and we'll sort it out. You don't need to buy again.`;
    case "handle_on_own_seat":
      return `${at} is on an earlier Coach of yours. Contact support and we'll move it to this one.`;
    case "handle_taken_account":
      return `${at} is linked to a different BMC account. Sign in with that account's email, or contact support if it's yours.`;
    case "handle_is_current":
      return `Your Coach is already built on ${at}.`;
    case "private_account":
      return `${at} is private, so your Coach can't learn from it. Make it public on TikTok, then try again.`;
    case "not_found":
      return `We couldn't find a public TikTok account called ${at}. Check the spelling and try again.`;
    case "lookup_unavailable":
    case "lookup_failed":
      return "We couldn't reach TikTok just now. Please try again in a moment.";
    case "building":
      return "Your Coach is being built right now. Try again when it finishes, in about 15 minutes.";
    case "change_limit":
      return "You changed your TikTok account recently. Each change rebuilds your Coach, so changes are limited. Contact support if you need another one.";
    case "not_active":
      return "Your subscription isn't active, so the account can't be changed. Check the Billing page.";
  }
  // Older workers send no codes: keep the profile-specific 404.
  if (e.status === 404) return `We couldn't find a public TikTok account called ${at}. Check the spelling and try again.`;
  return null;
}
const PRODUCTION_COACH_ORIGIN = "https://coach.brandsmeetcreators.com";

/**
 * Which coach worker this build talks to, as an origin (no path).
 *
 * Production is the only environment where defaulting is correct, so any other
 * build must name its backend explicitly via VITE_COACH_API_BASE. A staging
 * bundle silently falling back to production is the exact accident the staging
 * tier exists to prevent — one "Change TikTok account" click there would
 * rebuild a real customer's DNA. Returns null instead of throwing: this module
 * is evaluated even in demo builds, which must keep working.
 */
function resolveCoachOrigin(): string | null {
  // Optional chaining on `env` itself: this module is also bundled for plain
  // Node by scripts/test-coach.mjs, where import.meta.env does not exist.
  const configured = import.meta.env?.VITE_COACH_API_BASE?.trim();
  if (configured) {
    try {
      const url = new URL(configured);
      return  url.origin ;
      // return url.protocol === "https:" ? url.origin : null;
    } catch {
      return null;
    }
  }
  return import.meta.env?.VITE_APP_ENV === "production"
    ? PRODUCTION_COACH_ORIGIN
    : null;
}
const COACH_ORIGIN = resolveCoachOrigin();

/** Where an email's /go/:code short link resolves (the coach worker), or null if unusable. */
export function shortLinkTarget(code: string): string | null {
  if (!COACH_ORIGIN || !/^[A-Za-z0-9]{6,32}$/.test(code)) return null;
  return `${COACH_ORIGIN}/go/${code}`;
}

/**
 * The two Coach plans, as shown on the worker's /checkout page
 * (bmc-coach-mcp src/landing.ts checkoutPage). Display only: Whop charges
 * whatever the plan id on the worker says, so keep these in sync with it.
 */
export const COACH_PLANS = [
  { id: "annual", label: "1-year plan", price: "$247", per: "/year", note: "$20.58 a month · best value, save 56%" },
  { id: "monthly", label: "Monthly plan", price: "$47", per: "/month", note: "Cancel anytime" },
] as const;
export type CoachPlanId = (typeof COACH_PLANS)[number]["id"];

/**
 * Checkout on this build's coach worker, locked to the signed-in email. The
 * email rides in the fragment, so it never reaches a server log, Whop or
 * analytics; the checkout page moves it into its own tab storage and strips it.
 * Null when this build has no coach backend configured.
 */
export function coachCheckoutUrl(plan: CoachPlanId, email?: string | null): string | null {
  if (!COACH_ORIGIN) return null;
  const url = new URL(COACH_ORIGIN + "/checkout");
  url.searchParams.set("plan", plan);
  if (email) url.hash = "e=" + encodeURIComponent(email.trim());
  return url.href;
}

export function safeExternalUrl(raw: string, kind: "connector" | "billing") {
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    // The connector is served by the same worker as the API, so it must match
    // whichever origin this build is pointed at. An unconfigured non-production
    // build resolves to null and rejects everything, rather than quietly
    // accepting a production connector link.
    if (kind === "connector" && (!COACH_ORIGIN || url.origin !== COACH_ORIGIN || url.pathname === "/" || url.search || url.hash))
      return null;
    if (
      kind === "billing" &&
      url.hostname !== "whop.com" &&
      !url.hostname.endsWith(".whop.com")
    )
      return null;
    return url.href;
  } catch {
    return null;
  }
}
/**
 * `onUnauthorized` runs when the worker rejects the session (401): it should
 * refresh the session and return the new access token, or null when the
 * session is gone for good. The request is retried once with a new token.
 */
/**
 * The sign-in page's "Email me a sign-in link", sent by the coach worker
 * (POST /portal/sign-in) so it is our branded email through Resend. Needs no
 * session. Returns "unavailable" when this build has no worker or the worker
 * cannot take it, so the page can fall back to Supabase's own email.
 */
export type SignInRequestResult =
  | { status: "sent" }
  | { status: "no_account" }
  | { status: "cooldown"; retryAfter: number }
  | { status: "error"; message: string }
  | { status: "unavailable" };

export async function requestSignInLink(email: string, transport: typeof fetch = fetch): Promise<SignInRequestResult> {
  if (!COACH_ORIGIN) return { status: "unavailable" };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const r = await transport(COACH_ORIGIN + "/portal/sign-in", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      signal: controller.signal,
      cache: "no-store",
    });
    let body: { code?: unknown; retry_after?: unknown } = {};
    try { body = await r.json(); } catch { /* not JSON */ }
    if (r.ok) return { status: "sent" };
    const code = typeof body.code === "string" ? body.code : "";
    const retry = Number(body.retry_after);
    if (code === "no_account") return { status: "no_account" };
    if (code === "invalid_email") return { status: "error", message: "Please enter a valid email address." };
    if (r.status === 429)
      return Number.isFinite(retry) && retry > 0 && code === "cooldown"
        ? { status: "cooldown", retryAfter: Math.ceil(retry) }
        : { status: "error", message: "Too many requests. Wait a few minutes, then try again." };
    if (code === "quota") return { status: "error", message: "Our email service is busy right now. Please try again in a few minutes." };
    // 404 "not available" (the flow is off on this worker) or a server fault.
    return { status: "unavailable" };
  } catch {
    return { status: "unavailable" };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Portal "Forgot password": the coach worker emails a reset link on our domain
 * (Supabase's own reset email follows the brand dashboard's shared Site URL).
 * "unavailable" means fall back to Supabase's email. The worker answers an
 * unknown email like a sent one, so there is no "no_account" here.
 */
export async function requestPasswordReset(email: string, transport: typeof fetch = fetch): Promise<SignInRequestResult> {
  if (!COACH_ORIGIN) return { status: "unavailable" };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const r = await transport(COACH_ORIGIN + "/portal/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      signal: controller.signal,
      cache: "no-store",
    });
    let body: { code?: unknown; retry_after?: unknown } = {};
    try { body = await r.json(); } catch { /* not JSON */ }
    if (r.ok) return { status: "sent" };
    const code = typeof body.code === "string" ? body.code : "";
    const retry = Number(body.retry_after);
    if (code === "invalid_email") return { status: "error", message: "Please enter a valid email address." };
    if (r.status === 429)
      return Number.isFinite(retry) && retry > 0 && code === "cooldown"
        ? { status: "cooldown", retryAfter: Math.ceil(retry) }
        : { status: "error", message: "Too many requests. Wait a few minutes, then try again." };
    return { status: "unavailable" };
  } catch {
    return { status: "unavailable" };
  } finally {
    clearTimeout(timer);
  }
}

export function createCoachClient(
  getToken: () => Promise<string | null>,
  transport: typeof fetch = fetch,
  onUnauthorized?: () => Promise<string | null>,
) {
  async function request<T>(
    path: string,
    body?: unknown,
    query?: Record<string, string>,
    retryToken?: string,
  ): Promise<T> {
    const token = retryToken ?? (await getToken());
    if (!token)
      throw new CoachError("Please sign in again to manage your coach.", 401);
    if (!COACH_ORIGIN)
      throw new CoachError(
        "This build has no coach backend configured. Set VITE_COACH_API_BASE to the worker origin for this environment.",
        500,
      );
    // Built through URL so query values are encoded rather than concatenated —
    // a handle reaches this from a free-text field.
    const endpoint = new URL(COACH_ORIGIN + "/api/self/" + path);
    for (const [key, value] of Object.entries(query ?? {}))
      endpoint.searchParams.set(key, value);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await transport(
        endpoint.href,
        {
          method: body === undefined ? "GET" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
          signal: controller.signal,
          cache: "no-store",
        },
      );
      // The session was revoked elsewhere (a sign-out in the BMC app ends
      // every session) or its access token went stale: refresh once, retry.
      if (response.status === 401 && !retryToken && onUnauthorized) {
        clearTimeout(timer);
        const fresh = await onUnauthorized().catch(() => null);
        if (fresh && fresh !== token) return request<T>(path, body, query, fresh);
      }
      if (!response.ok) {
        // Only the reason code is read from an error body, and only if it has
        // the shape of one; the server's own text never reaches the creator.
        let code: string | undefined;
        try {
          const c = ((await response.json()) as { code?: unknown })?.code;
          if (typeof c === "string" && /^[a-z_]{2,40}$/.test(c)) code = c;
        } catch {
          /* not JSON */
        }
        throw new CoachError(
          response.status === 401
            ? "Please sign in again to manage your coach."
            : response.status === 404
              ? "We could not find a coach linked to this account."
              : response.status === 429
                ? "A few too many requests. Please wait a moment and try again."
                : "We could not complete that request. Please try again.",
          response.status,
          code,
        );
      }
      try {
        return (await response.json()) as T;
      } catch {
        throw new CoachError(
          "We could not read the response. Please try again.",
          502,
        );
      }
    } catch (e) {
      if (e instanceof CoachError) throw e;
      throw new CoachError(
        "We could not reach your coach. Check your connection and try again.",
        0,
      );
    } finally {
      clearTimeout(timer);
    }
  }
  return {
    /** `fresh` also asks the worker to re-check the TikTok link instead of using its cache. */
    status: (opts?: { fresh?: boolean }) =>
      request<CoachStatus>("coach-status", undefined, opts?.fresh ? { fresh: "1" } : undefined),
    /**
     * Confirm a typed username against the public profile before anything is
     * built. 404 here means "no such TikTok profile", not "no seat", so the
     * shared 404 copy would be actively misleading — restate it.
     */
    lookupHandle: async (handle: string) => {
      const normalized = normalizeHandle(handle);
      try {
        return await request<HandleLookup>("lookup-handle", undefined, {
          handle: normalized,
        });
      } catch (e) {
        if (!(e instanceof CoachError)) throw e;
        const message = handleMessage(e, normalized);
        throw message ? new CoachError(message, e.status, e.code) : e;
      }
    },
    /**
     * Sets the first handle, or changes it (a full rebuild). The worker's
     * refusals are specific and the shared copy for 409 / 429 would be wrong,
     * so they are restated here — from fixed text, never the server's body.
     */
    fixHandle: async (handle: string) => {
      const normalized = normalizeHandle(handle);
      try {
        return await request<{ status: "reprocessing"; eta_minutes: number }>("fix-handle", {
          new_handle: normalized,
        });
      } catch (e) {
        if (!(e instanceof CoachError)) throw e;
        // A 404 here means no seat, not no TikTok: only coded refusals get handle copy.
        const message =
          (e.code ? handleMessage(e, normalized) : null) ??
          (e.status === 409
            ? "We could not switch to that account right now. It may already be linked to another Coach, or your Coach is being built. Try again in a few minutes, or contact support."
            : e.status === 429
              ? "You changed your TikTok account recently. Each change rebuilds your Coach, so changes are limited. Contact support if you need another one."
              : null);
        throw message ? new CoachError(message, e.status, e.code) : e;
      }
    },
    resend: () => request<{ sent: true; to: string }>("resend-setup-email", {}),
    briefs: (brief: Partial<Brief> & Pick<Brief, "type" | "enabled">) =>
      request<{ ok: true; briefs: Brief[] }>("briefs", brief),
    billing: () => request<{ url: string }>("billing-portal", {}),
    subscription: () => request<BillingSummary>("billing"),
    /** `fresh` re-reads Whop's totals instead of the 15-minute cache. */
    affiliate: (opts?: { fresh?: boolean }) =>
      request<AffiliateSummary>("affiliate", undefined, opts?.fresh ? { fresh: "1" } : undefined),
    /** Cancels at the end of the paid period. The worker never cancels immediately. */
    cancelSubscription: async (reason: CancelReason, otherText?: string) => {
      try {
        return await request<BillingSummary>("cancel-subscription", { reason, other_text: otherText?.trim() || undefined });
      } catch (e) {
        if (e instanceof CoachError && e.status === 422)
          throw new CoachError(reason === "other" ? "Tell us your reason in a few words, then try again." : "Choose a reason, then try again.", 422);
        if (e instanceof CoachError && e.status === 409)
          throw new CoachError("This subscription can't be cancelled here right now. Refresh the page, or contact support.", 409);
        if (e instanceof CoachError && e.status === 502)
          throw new CoachError("Whop didn't confirm the cancellation, so nothing changed. Please try again in a minute.", 502);
        throw e;
      }
    },
    resumeSubscription: async () => {
      try {
        return await request<BillingSummary>("resume-subscription", {});
      } catch (e) {
        if (e instanceof CoachError && e.status === 409)
          throw new CoachError("There's no pending cancellation to undo. Refresh the page to see the latest status.", 409);
        if (e instanceof CoachError && e.status === 502)
          throw new CoachError("Whop didn't confirm the change, so your subscription is still set to cancel. Please try again in a minute.", 502);
        throw e;
      }
    },
  };
}
